import { useState, createContext, useContext } from "react";
import { Info } from "lucide-react";

export const ThemeContext = createContext({});
export const useTheme = () => useContext(ThemeContext);

export function Section({ num, title, subtitle, children, theme: propTheme }) {
  const ctxTheme = useTheme();
  const t = propTheme || ctxTheme;
  const numBg = t?.numBg || "#D4A373";
  const numText = t?.numText || "#0F172A";
  return (
    <div className="bg-white border border-[#CBD5E1] mb-7" data-testid={`section-${num}`}>
      <div className="bg-[#0F172A] flex items-center gap-3.5 px-6 py-3.5">
        <span className="text-[0.7rem] font-bold px-2 py-0.5"
          style={{ background: numBg, color: numText, letterSpacing: "0.1em" }}>{num}</span>
        <span className="font-playfair text-base font-semibold text-white">{title}</span>
        {subtitle && <span className="text-[0.75rem] text-[#94A3B8] ml-auto">{subtitle}</span>}
      </div>
      <div className="px-7 py-6">
        <div className="space-y-5">{children}</div>
      </div>
    </div>
  );
}

export function DarkSection({ num, title, children }) {
  return (
    <div className="bg-[#0F172A] border-none mb-7" data-testid={`section-${num || "bonus"}`}>
      <div className="bg-[#D4A373] flex items-center gap-3.5 px-6 py-3.5">
        <span className="bg-[#0F172A] text-[#D4A373] text-[0.7rem] font-bold px-2 py-0.5"
          style={{ letterSpacing: "0.1em" }}>{num || "★"}</span>
        <span className="font-playfair text-base font-semibold text-[#0F172A]">{title}</span>
      </div>
      <div className="p-7">
        <div className="space-y-5">{children}</div>
      </div>
    </div>
  );
}

export function Question({ label, text, hint, info, children, theme: propTheme }) {
  const ctxTheme = useTheme();
  const t = propTheme || ctxTheme;
  const labelColor = t?.label || "#B5835A";
  return (
    <div>
      <span className="text-[0.75rem] font-semibold uppercase mb-1 flex items-center gap-1.5"
        style={{ letterSpacing: "0.2em", color: labelColor }}>
        {label}
        {info && <InfoTip text={info} />}
      </span>
      {text && <p className="text-[0.9375rem] font-medium text-[#1E293B] mb-2 leading-snug">{text}</p>}
      {hint && <p className="text-[0.8125rem] text-[#94A3B8] mb-2 italic">{hint}</p>}
      {children}
    </div>
  );
}

export function InfoTip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block">
      <button type="button" onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        className="text-[#94A3B8] hover:text-[#D4A373] transition-colors" aria-label="Info">
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-[#0F172A] text-[#F1F5F9] text-[0.75rem] font-normal normal-case px-3 py-2 leading-relaxed z-50 shadow-lg"
          style={{ letterSpacing: "0" }}>
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0F172A]" />
        </span>
      )}
    </span>
  );
}

export function CheckboxGroup({ fieldKey, options, otherKey, formData, toggleCheckbox, updateField, theme: propTheme }) {
  const ctxTheme = useTheme();
  const t = propTheme || ctxTheme;
  const accent = t?.accent || "#D4A373";
  const isChecked = (opt) => (formData[fieldKey] || []).includes(opt);
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {options.map(opt => (
        <label key={opt}
          className="flex items-center gap-2 text-sm font-medium cursor-pointer px-3 py-1.5 border transition-all"
          style={{
            borderColor: isChecked(opt) ? accent : "#CBD5E1",
            background: isChecked(opt) ? "#fff" : "#F8F9FA",
            color: isChecked(opt) ? "#0F172A" : "#1E293B",
          }}
          data-testid={`checkbox-${fieldKey}-${opt.replace(/\s/g, "-").toLowerCase()}`}>
          <input type="checkbox" checked={isChecked(opt)}
            onChange={() => toggleCheckbox(fieldKey, opt)} style={{ accentColor: accent }} />
          {opt}
        </label>
      ))}
      {otherKey && isChecked("Anderes") && (
        <input type="text" value={formData[otherKey] || ""}
          onChange={e => updateField(otherKey, e.target.value)}
          placeholder="Anderes / Details …" className="portal-input mt-2 w-full" />
      )}
    </div>
  );
}

export function SelectField({ fieldKey, options, formData, updateField, placeholder }) {
  return (
    <select value={formData[fieldKey] || ""} onChange={e => updateField(fieldKey, e.target.value)}
      className="portal-input" data-testid={`field-${fieldKey}`}>
      <option value="">{placeholder || "Bitte wählen …"}</option>
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  );
}

export function PriorityRow({ fieldKey, badge, placeholder, formData, updateField }) {
  return (
    <div className="grid items-center gap-2" style={{ gridTemplateColumns: "auto 1fr" }}>
      <span className="text-[0.6875rem] font-bold uppercase bg-[#0F172A] text-[#D4A373] px-2 py-1 text-center min-w-[1.75rem]"
        style={{ letterSpacing: "0.1em" }}>{badge}</span>
      <input type="text" className="portal-input" value={formData[fieldKey] || ""}
        onChange={e => updateField(fieldKey, e.target.value)} placeholder={placeholder}
        data-testid={`field-${fieldKey}`} />
    </div>
  );
}

export function WorkshopNote({ text }) {
  return (
    <div className="bg-[#FFF7ED] border-[1.5px] border-[#D4A373] px-5 py-3 mb-5 text-sm font-medium text-[#1E293B]">
      {text}
    </div>
  );
}

export function DarkInput({ fieldKey, placeholder, formData, updateField }) {
  return (
    <input type="text" value={formData[fieldKey] || ""}
      onChange={e => updateField(fieldKey, e.target.value)} placeholder={placeholder}
      className="w-full border-[1.5px] border-[#334155] bg-[#1E293B] text-[#F1F5F9] font-sans text-[0.9375rem] px-3.5 py-2.5 outline-none transition-colors focus:border-[#D4A373]"
      data-testid={`field-${fieldKey}`} />
  );
}

export function DarkTextarea({ fieldKey, placeholder, formData, updateField, tall }) {
  return (
    <textarea value={formData[fieldKey] || ""}
      onChange={e => updateField(fieldKey, e.target.value)} placeholder={placeholder}
      className={`w-full border-[1.5px] border-[#334155] bg-[#1E293B] text-[#F1F5F9] font-sans text-[0.9375rem] px-3.5 py-2.5 outline-none transition-colors focus:border-[#D4A373] resize-vertical ${tall ? "min-h-[110px]" : "min-h-[72px]"}`}
      data-testid={`field-${fieldKey}`} />
  );
}
